import {
  APP_RELEASE_DATE,
  APP_VERSION,
  APP_VERSION_LABEL,
} from './appVersion';

export type ChangelogCategory = 'added' | 'changed' | 'fixed' | 'security';

export const CHANGELOG_CATEGORY_LABELS: Record<ChangelogCategory, string> = {
  added: 'جدید',
  changed: 'تغییر',
  fixed: 'رفع',
  security: 'امنیت',
};

export interface ChangelogItem {
  /** متن توضیح */
  text: string;
  /** برچسب کوتاه — در UI به‌صورت badge نمایش داده می‌شود */
  label?: string;
  /** آیتم‌های مهم برجسته‌تر نمایش داده می‌شوند */
  important?: boolean;
}

export interface ChangelogVersion {
  version: string;
  versionLabel: string;
  releaseDate: string;
  title: string;
  summary?: string;
  /** موضوعات کلیدی نسخه — badge در بالای کارت */
  highlights?: string[];
  sections: Partial<Record<ChangelogCategory, ChangelogItem[]>>;
}

/** shorthand برای ساخت آیتم changelog */
export function changelogItem(
  text: string,
  options?: { label?: string; important?: boolean },
): ChangelogItem {
  return { text, ...options };
}

export const CHANGELOG_VERSIONS: ChangelogVersion[] = [
  {
    version: APP_VERSION,
    versionLabel: APP_VERSION_LABEL,
    releaseDate: APP_RELEASE_DATE,
    title: 'نبض بازار، دسترسی امن‌تر و تجربه بهتر',
    summary:
      'در این نسخه داشبورد نبض بازار تکمیل شده، کنترل دسترسی به داده شعب تقویت شده، راهنمای نمودارها گسترش یافته و مشکلات نمایش در موبایل و Safari برطرف شده است.',
    highlights: ['نبض بازار', 'کنترل دسترسی', 'شاخص اطمینان', 'راهنمای نمودارها'],
    sections: {
      added: [
        changelogItem('مقایسه فروش، تعداد فاکتور، قیمت و سبد خرید با میانگین بازار', {
          label: 'نبض بازار',
          important: true,
        }),
        changelogItem('نشان دادن قابل اتکا بودن مقایسه کنار KPIها', {
          label: 'شاخص اطمینان',
          important: true,
        }),
        changelogItem('ثبت‌نام برای مشتریانی که هنوز دسترسی کامل ندارند', {
          label: 'لیست انتظار',
        }),
        changelogItem('صفحه معرفی قبل از فعال‌سازی کامل دسترسی', {
          label: 'پیش‌نمایش نبض بازار',
        }),
        changelogItem('دکمه راهنما و توضیحات در نمودارهای داشبوردهای مختلف', {
          label: 'راهنمای نمودارها',
          important: true,
        }),
        changelogItem('امکان درخواست مشاوره مستقیم از داخل محصول', {
          label: 'مشاوره',
        }),
        changelogItem('اطلاع‌رسانی خودکار به تیم پس از ثبت‌نام در لیست انتظار'),
        changelogItem('دسترسی ویژه برای مشاهده سند محصول و بخش‌های مدیریتی', {
          label: 'مدیر میز',
        }),
        changelogItem('دکمه میانبر راهنما در داشبورد باشگاه مشتریان'),
        changelogItem('دکمه لینک‌های مرتبط در برخی داشبوردها'),
        changelogItem('صفحه تاریخچه تغییرات نسخه‌ها در منوی مستندات', {
          label: 'Changelog',
        }),
      ],
      changed: [
        changelogItem('هر کاربر فقط داده شعب مجاز خود را می‌بیند', {
          label: 'کنترل دسترسی شعبه',
          important: true,
        }),
        changelogItem('بهبود جریان ورود و انتقال بین محیط اصلی و staging', {
          label: 'احراز هویت',
        }),
        changelogItem('بازطراحی KPI، جدول مقایسه و نمودار روند', {
          label: 'نبض بازار',
        }),
        changelogItem('کنترل‌های جدید و راهنمای بهتر در نمودارهای میله‌ای و خطی', {
          label: 'نمودارها',
        }),
        changelogItem('بهبود نمودارهای شروط باشگاه و محاسبه رشد', {
          label: 'باشگاه مشتریان',
        }),
        changelogItem('بهبود نمایش و فیلترها', { label: 'گروه‌بندی مشتریان' }),
        changelogItem('بهبود جدول مقایسه شعب', { label: 'نمای کلی برند' }),
        changelogItem('نمایش واضح‌تر وضعیت دسترسی به قابلیت‌ها', { label: 'لایسنس' }),
        changelogItem('بهبود رفتار در حالت چندشعبه‌ای', { label: 'انتخاب شعبه و برند' }),
        changelogItem('به‌روزرسانی badge نسخه و آیتم‌های منو', { label: 'هدر و منو' }),
        changelogItem('بهبود تجربه فیلتر در موبایل', { label: 'فیلتر موبایل' }),
        changelogItem('بهبود مدیریت بازخوردها', { label: 'بازخورد ادمین' }),
        changelogItem('به‌روزرسانی محتوای سند محصول نبض بازار'),
      ],
      fixed: [
        changelogItem('رفع مشکلات نمایش در Safari و iOS', { label: 'Safari', important: true }),
        changelogItem('بهبود سازگاری موبایل در layout کلی', { label: 'موبایل' }),
        changelogItem('رفع مشکلات جزئی در جدول‌ها و tooltipها'),
        changelogItem('بهبود نمایش نمودارها در داشبورد شروط باشگاه'),
      ],
      security: [
        changelogItem('بررسی مجوز دسترسی در همه درخواست‌های داده', {
          label: 'دسترسی شعبه',
          important: true,
        }),
        changelogItem('محدودسازی بخش‌های حساس فقط برای نقش‌های مجاز', {
          label: 'دسترسی مدیریتی',
          important: true,
        }),
      ],
    },
  },
  {
    version: '2.0.0',
    versionLabel: 'نسخه ۲',
    releaseDate: '۸ شهریور ۱۴۰۵',
    title: 'نگاه هوشمند، گسترده‌تر و کاربردی‌تر',
    summary:
      'نسخه ۲ گام بزرگی در گسترش داشبوردها، مقایسه با بازار، تحلیل چندشعبه‌ای برند و بهبود تجربه کاربری بود — همه داشبوردهای قبلی حفظ شدند.',
    highlights: ['داشبوردهای جدید', 'نبض بازار', 'نمای برند', 'راهنمای تعاملی'],
    sections: {
      added: [
        changelogItem('نسخه اولیه مقایسه با بازار', { label: 'نبض بازار', important: true }),
        changelogItem('روند فروش و دسته‌بندی محصولات', { label: 'داشبورد فروش', important: true }),
        changelogItem('الگوی بازگشت مشتریان در شیفت‌ها و انواع سفارش', {
          label: 'اشتراک‌گیری',
          important: true,
        }),
        changelogItem('شاخص‌های کلیدی و مقایسه شعب برای برندهای چندشعبه‌ای', {
          label: 'نمای کلی برند',
          important: true,
        }),
        changelogItem('تحلیل رشد گروه‌های خرید و مشتریان جدید/بازگشتی', {
          label: 'رفتار خرید',
        }),
        changelogItem('توزیع از تک‌خرید تا وفادار', { label: 'گروه‌بندی مشتریان' }),
        changelogItem('جدول رشد و راهنمای تعاملی', { label: 'ارزیابی هوشمند' }),
        changelogItem('معرفی محصول به‌صورت اسلایدی', { label: 'ارائه نگاه هوشمند' }),
        changelogItem('شاخص‌های benchmark کل بازار', { label: 'بنچمارک بازار' }),
        changelogItem('نمایش تاریخ آخرین بروزرسانی داده در بالای داشبوردها', {
          label: 'تازگی داده',
        }),
        changelogItem('تور گام‌به‌گام در داشبوردهای کلیدی', {
          label: 'راهنمای تعاملی',
          important: true,
        }),
        changelogItem('توضیح ستون‌ها در جداول پیچیده', { label: 'راهنمای ستون‌ها' }),
      ],
      changed: [
        changelogItem('انتقال داشبوردها به ساختار componentized'),
        changelogItem('افزودن بخش‌های بازار، برند و باشگاه', { label: 'منوی Sidebar' }),
        changelogItem('گزینه «۱ ماه اخیر» در فیلتر زمانی', { label: 'فیلتر زمانی' }),
        changelogItem('مسیر ارزیابی هوشمند به عملکرد ماهانه تغییر کرد'),
        changelogItem('خوانایی بهتر در موبایل', { label: 'طراحی' }),
      ],
      fixed: [changelogItem('بهبود ARIA و ناوبری کیبورد در تب‌ها', { label: 'دسترسی‌پذیری' })],
    },
  },
];

export const CURRENT_CHANGELOG = CHANGELOG_VERSIONS[0];

/** @deprecated از CHANGELOG_VERSIONS استفاده کنید */
export const RELEASE_HISTORY = CHANGELOG_VERSIONS;
